import {
  Box,
  Button,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  TablePagination,
} from "@mui/material";
import React, { useState, useCallback } from "react";
import { RightPanel } from "../../../../components/RightPanel";
import { saveMarketCampaignAudienceListAsyncV2 } from "../../../../redux/marketCampaign/marketCampaignActions";
import { useBillingCompanyContext } from "../../../../providers";
import { useDispatchAction } from "../../../../redux/store.hooks";
import { IMarketCampaignAudience } from "../../../../redux/marketCampaign/marketCampaign.types";
import { useFormik } from "formik";
import { ICreateNewAudience } from "./CreateAudienceRightPanel.types";
import {
  Step1UploadAndDetails,
  Step2ExtractHeaders,
  Step3SetupMapping,
} from "./components";
import { validateRowForStats } from "./helpers/validationHelpers";
import { validateRowForStatsByMappings } from "./helpers/validationHelpers";
import {
  generateFile,
  filterValidRows,
  FileType,
} from "./helpers/fileGenerator";
import { IssuesDialog } from "./dialogs/IssuesDialog";
import { showMessage } from "../../../../redux/messages/messagesActions";
import { transformDataWithMappings } from "./helpers/transformDataWithMappings";
import { getFileExtension } from "../../../../components/FileUploadIcon/helpers";

interface ICreateAudienceRightPanelProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (data: IMarketCampaignAudience) => void;
  heading?: string;
}

const CREATE_AUDIENCE_STEPS = [
  "Audience Details",
  "Setup Mapping",
  "Extract Headers",
];
const MANDATORY_FIELDS = ["phone"];
export const CreateAudienceRightPanel: React.FC<
  ICreateAudienceRightPanelProps
> = ({ open, onClose, onSuccess, heading }) => {
  const dispatch = useDispatchAction();
  const companyOutlet = useBillingCompanyContext();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showIssuesDialog, setShowIssuesDialog] = useState(false);
  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    submitForm,
    setValues,
    setFieldValue,
  } = useFormik<ICreateNewAudience>({
    initialValues: {
      audience_name: "",
      source_link: "",
      current_step: 0,
      headers: [],
      mappings: [],
      isMappingCompleted: false,
      sample_data: [],
    },
    validate: (values) => {
      const errors: any = {};
      const audienceName = (values.audience_name ?? "").trim();
      if (!audienceName) {
        errors.audience_name = "Audience name is required";
      } else if (!/^[A-Za-z ]+$/.test(audienceName)) {
        errors.audience_name = "Only letters and spaces are allowed";
      }
      return errors;
    },
    onSubmit: async (values) => {
      // 1. Transform data using mappings
      const { headers, data } = transformDataWithMappings(
        values.sample_data,
        values.mappings
      );

      // 2. Generate Excel using mapped headers
      const fileExt = getFileExtension(values.source_link);
      const fileType: FileType = fileExt === "csv" ? "csv" : "excel";

      const excelFile = await generateFile({
        headers,
        data,
        filename: values.audience_name || "audience_data",
        fileType,
      });

      const mapping_keys = values.mappings
        .filter((mapping) => mapping.isMapped && mapping.selectedMapping)
        .map((mapping) => ({
          check: mapping.isChecked ? 1 : 0,
          label: mapping.selectedMapping,
          column: mapping.header,
        }));

      const payload = {
        audience_name: values.audience_name,
        localFile: excelFile,
        // localFile: values.localFile,
        // source_link: values.source_link,
        audience_list: [],
        company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
        status: "ACTIVE",
        market_campaign_audience_list_uuid: null,
        mapping_keys: mapping_keys,
      };

      dispatch(
        saveMarketCampaignAudienceListAsyncV2(payload, (isSuccess, data) => {
          if (isSuccess && data) onSuccess(data);
        })
      );
    },
  });

  const isAllMandatoryFieldsMapped = MANDATORY_FIELDS.every((field) =>
    values.mappings.some((item) => item.selectedMapping === field)
  );

  const [validationStats, setValidationStats] = useState({
    total: 0,
    valid: 0,
    issues: 0,
  });

  const handleValidationStatsUpdate = useCallback(
    (stats: { total: number; valid: number; issues: number }) => {
      setValidationStats(stats);
    },
    []
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSetupMappingClick = () => {
    setFieldValue("current_step", 2);
  };

  const handleProceedWithValidRows = async () => {
    try {
      const validRows = filterValidRows(
        values.sample_data,
        values.headers,
        (row) => validateRowForStatsByMappings(row, values.mappings)
      );

      // const excelFile = await generateExcelFile({
      //   headers: values.headers,
      //   data: validRows,
      //   filename: values.audience_name || "audience_data",
      // });

      await setFieldValue("sample_data", validRows);
      // setFieldValue("localFile", excelFile);
      setShowIssuesDialog(false);
      await submitForm();
    } catch (error: any) {
      dispatch(
        showMessage({
          type: "error",
          message: error.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

  const handleDialogClose = () => {
    setShowIssuesDialog(false);
  };

  const handleSaveClick = () => {
    if (validationStats.issues > 0) {
      setShowIssuesDialog(true);
      return;
    }
    submitForm();
  };

  return (
    <>
      <RightPanel
        heading={heading || ""}
        open={open}
        width="100%"
        isWrappedWithForm
        onFormSubmit={handleSubmit}
        onClose={onClose}
        actionButtons={() => {
          if (values.current_step === 0) {
            return (
              <Stack direction={"row"} justifyContent={"flex-end"} spacing={2}>
                <Button
                  variant="contained"
                  onClick={() => setFieldValue("current_step", 1)}
                  disabled={!values.source_link || !values.audience_name}
                >
                  Setup Mapping
                </Button>
              </Stack>
            );
          }
          if (values.current_step === 1) {
            return (
              <Stack direction={"row"} justifyContent={"flex-end"} spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => setFieldValue("current_step", 0)}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSetupMappingClick}
                  disabled={!isAllMandatoryFieldsMapped}
                >
                  Extract Headers
                </Button>
              </Stack>
            );
          }
          return (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total rows: {validationStats.total} | Valid rows:{" "}
                  {validationStats.valid} | Rows with issues:{" "}
                  {validationStats.issues}
                </Typography>

                <TablePagination
                  component="div"
                  count={validationStats.total}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Rows per page:"
                  sx={{
                    "& .MuiTablePagination-toolbar": {
                      paddingLeft: 2,
                      paddingRight: 2,
                    },
                  }}
                />
              </Box>

              <Box sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  spacing={2}
                >
                  <Button
                    variant="outlined"
                    onClick={() => setFieldValue("current_step", 1)}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveClick}
                    disabled={!isAllMandatoryFieldsMapped}
                  >
                    Save
                  </Button>
                </Stack>
              </Box>
            </>
          );
        }}
      >
        <Box
          sx={{
            width: "100%",
            mb: 4,
          }}
        >
          <Stepper activeStep={values.current_step} alternativeLabel>
            {CREATE_AUDIENCE_STEPS.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  onClick={() => setFieldValue("current_step", index)}
                  sx={{
                    cursor: "pointer",
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        {values.current_step === 0 && (
          <Step1UploadAndDetails
            values={values}
            handleChange={handleChange}
            setValues={setValues}
            errors={errors}
            setFieldValue={setFieldValue}
          />
        )}
        {values.current_step === 1 && (
          <Step3SetupMapping
            headers={values.headers}
            data={values.sample_data}
            mappings={values.mappings}
            onMappingChange={(newMappings) =>
              setFieldValue("mappings", newMappings)
            }
          />
        )}
        {values.current_step === 2 && (
          <Step2ExtractHeaders
            headers={values.headers}
            data={values.sample_data}
            mappings={values.mappings}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onValidationStatsUpdate={handleValidationStatsUpdate}
          />
        )}
      </RightPanel>

      <IssuesDialog
        open={showIssuesDialog}
        onClose={handleDialogClose}
        onProceed={handleProceedWithValidRows}
        issuesCount={validationStats.issues}
        validCount={validationStats.valid}
      />
    </>
  );
};
